import React, { useEffect, useState, useCallback } from "react";
import { Shield, Wrench } from "lucide-react";
import "./RoleToolPermissionModal.css";
import customFetch from "../../../../helpers/axios";
import ButtonClose from "../../../components/Buttons/ButtonClose/ButtonClose";
import { useTranslation } from "react-i18next";
import useUser from "../../../../hooks/useUser";

export default function ToolPermissionModal({
  isOpen,
  onClose,
  permission,
}) {
  const { t } = useTranslation('permissions');
  const { user } = useUser();
  const accessToken = user?.access;
  const [permissionData, setPermissionData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch complete permission data for viewing
  const fetchPermissionDetails = useCallback(
    async (permissionId) => {
      try {
        if (!accessToken) return;

        setIsLoading(true);
        const resp = await customFetch.get(
          `/api/auth/role-tool-permissions/${permissionId}/`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        setPermissionData(resp.data);
      } catch (e) {
        console.error("Failed to fetch permission details", e);
        setPermissionData(null);
      } finally {
        setIsLoading(false);
      }
    },
    [accessToken]
  );

  useEffect(() => {
    if (!isOpen) return;

    if (permission && permission.id) {
      fetchPermissionDetails(permission.id);
    } else {
      setPermissionData(null);
      setIsLoading(false);
    }
  }, [isOpen, permission, fetchPermissionDetails]);

  const handleClose = () => {
    if (!isLoading) onClose();
  };

  if (!isOpen) return null;

  // Show loading while fetching permission details
  if (isLoading) {
    return (
      <div className="rtp-modal-overlay">
        <div className="rtp-modal">
          <div className="rtp-modal-header">
            <div className="rtp-modal-title-section">
              <Shield className="rtp-modal-icon" size={20} />
              <h2 className="rtp-modal-title">{t('modal.loadingTitle')}</h2>
            </div>
            <ButtonClose
              onClick={handleClose}
              className="rtp-close-btn"
              ariaLabel={t('modal.closeAria')}
              size={20}
            />
          </div>
          <div className="rtp-form" style={{ textAlign: "center", padding: "2rem" }}>
            <div>{t('modal.loadingDetails')}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!permissionData) {
    return null;
  }

  return (
    <div className="rtp-modal-overlay">
      <div className="rtp-modal">
        <div className="rtp-modal-header">
          <div className="rtp-modal-title-section">
            <Shield className="rtp-modal-icon" size={20} />
            <h2 className="rtp-modal-title">{t('modal.viewTitle')}</h2>
          </div>
          <ButtonClose
            onClick={handleClose}
            className="rtp-close-btn"
            ariaLabel={t('modal.closeAria')}
            size={20}
          />
        </div>

        {/* Creation date at top right */}
        <div className="rtp-creation-date">
          <span className="rtp-creation-label">{t('modal.fields.created')}: </span>
          <span className="rtp-creation-value">
            {permissionData.created ? new Date(permissionData.created).toLocaleDateString() : '-'}
          </span>
        </div>

        <div className="rtp-form">
          {/* Integration and Organization in same row */}
          <div className="rtp-field-row">
            <div className="rtp-field rtp-field-half">
              <div className="rtp-label-value">
                <span className="rtp-label-inline">{t('modal.fields.integration')}:</span>
                <span className="rtp-value-inline">
                  {permissionData.integration_name || permissionData.integration?.display_name || permissionData.integration?.name || 'No integration'}
                </span>
              </div>
            </div>
            <div className="rtp-field rtp-field-half">
              <div className="rtp-label-value">
                <span className="rtp-label-inline">{t('modal.fields.organization')}</span>
                <span className="rtp-value-inline">
                  {permissionData.organization?.name || user?.organization?.name || 'Aulasneo'}
                </span>
              </div>
            </div>
          </div>

          {/* Display Name (Nombre) and Name (Id Nombre) in same row */}
          <div className="rtp-field-row">
            <div className="rtp-field rtp-field-half">
              <div className="rtp-label-value">
                <span className="rtp-label-inline">{t('modal.fields.name')}</span>
                <span className="rtp-value-inline">{permissionData.display_name || '-'}</span>
              </div>
            </div>
            <div className="rtp-field rtp-field-half">
              <div className="rtp-label-value">
                <span className="rtp-label-inline">{t('modal.fields.displayName')}</span>
                <span className="rtp-value-inline">{permissionData.name || '-'}</span>
              </div>
            </div>
          </div>

          {/* Separator line */}
          <div className="rtp-separator"></div>

          {/* Tools section */}
          <div className="rtp-field">
            <label className="rtp-label">{t('modal.fields.tools')} ({permissionData.tools?.length || 0})</label>
            <div className="rtp-tools-list">
              {permissionData.tools && permissionData.tools.length > 0 ? (
                permissionData.tools.map((tool, index) => (
                  <div key={tool.id || index} className="rtp-tool-item-readonly">
                    <Wrench size={14} className="rtp-tool-icon" />
                    <span>{tool.display_name || tool.name || `Tool ${tool.id}`}</span>
                  </div>
                ))
              ) : (
                <div className="rtp-muted">{t('modal.fields.noToolsAssigned')}</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
